import ReactJson from "react-json-view";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { fetchBenefitPlanTableScan, fetchHrpTableItemsByHccID, fetchBenefitPlanTableItemsByPk, fetchHrpTableItemsBySk, fetchHrpTableItemsBySparse, fetchHrpTableScan, fetchItemsByRetryOn } from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash.debounce";
import { queryClient } from "../../App";
import DatePicker from "react-datepicker";
import ToggleSwitch from "../shared/ToggleSwitch";
import { getTimeDiff } from "../../utils/commonUtils";
import Select from 'react-select';

const searchOptions = [
    { value: 'clientId', label: 'clientId', additional: { pkKey: 'clientId', skKey: 'productType#planId#configType' } },
    { value: 'isFromIndex', label: 'isFromIndex', additional: { pkKey: 'isFrom', skKey: 'productType#planId#configType' } },
    { value: 'typeKeyClientIdIndex', label: 'typeKeyClientIdIndex', additional: { pkKey: 'typeKeyClientIdIndexPk', skKey: 'typeKeyClientIdIndexSk' } }
]

const byValue = (value) => searchOptions.find((o) => o.value === value)

const filterPresets = [
    {
        id: 'oe-configs',
        label: 'OE configs / networks',
        hint: 'All OE clients. Open a row to see networkConfigs[] (uuid, directoryType, codes).',
        optionValue: 'typeKeyClientIdIndex',
        pk: 'oe_config',
        sk: '',
        columns: ['clientId', 'productType#planId#configType', 'typeKey', 'networkConfigs', 'siteURL', 'publish'],
    },
    {
        id: 'oe-by-name',
        label: 'OE config by name',
        hint: 'SK = OE client name (ATT, DUNCT), not the HRP HCC. Networks live on that item.',
        optionValue: 'typeKeyClientIdIndex',
        pk: 'oe_config',
        sk: '',
        skPlaceholder: 'DUNCT',
        columns: ['clientId', 'productType#planId#configType', 'typeKey', 'networkConfigs', 'siteURL', 'publish'],
    },
    {
        id: 'hrp-plans',
        label: 'HRP plans / affiliation',
        hint: 'PK = HRP clientId (CV4CV, DUNCT). networkAffiliation is { clientName, uuid, networkName }.',
        optionValue: 'clientId',
        pk: '',
        pkPlaceholder: 'CV4CV',
        sk: 'Medical#HRP_',
        columns: ['clientId', 'productType#planId#configType', 'planId', 'planName', 'typeKey', 'networkAffiliation', 'isFrom'],
    },
    {
        id: 'hrp-accounts',
        label: 'HRP accounts',
        hint: 'isFrom=HRP, SK config_hrp_account. Account shells, not plan affiliation.',
        optionValue: 'isFromIndex',
        pk: 'HRP',
        sk: 'config_hrp_account',
        columns: ['clientId', 'productType#planId#configType', 'clientName', 'typeKey', 'isFrom'],
    },
    {
        id: 'jv-clients',
        label: 'Javelina clients',
        hint: 'clientId=ALL lists JV clients.',
        optionValue: 'clientId',
        pk: 'ALL',
        sk: '',
        columns: ['clientId', 'productType#planId#configType', 'clientName', 'planId', 'planName'],
    },
    {
        id: 'notify-defaults',
        label: 'Notification templates',
        hint: 'clientId=ALL, SK defaultNotificationConfig.',
        optionValue: 'clientId',
        pk: 'ALL',
        sk: 'defaultNotificationConfig',
        columns: ['clientId', 'productType#planId#configType', 'clientName'],
    },
]

const defaultColumns = ['clientId', 'productType#planId#configType', 'clientName', 'planId', 'planName', 'taxId', 'effectiveDate', 'completed', 'created', 'modified'];

const BenefitPlansTable = ({ subscriptionHccId, autoRefreshEnabled: parentAutoRefreshEnabled }) => {

    const now = new Date()

    const timezoneOffsetMs = now.getTimezoneOffset() * 60000;
    const localStartOfDayNow = new Date(now.getTime() - timezoneOffsetMs);
    localStartOfDayNow.setHours(0, 0, 0, 0);
    const [showMoreColumns, setShowMoreColumns] = useState(false);


    const [selectedSearchOption, setSelectedSearchOption] = useState(null);
    const [searchPkValue, setSearchPkValue] = useState();
    const [decouncedSearchPkValue, setDecouncedSearchPkValue] = useState();

    const [searchSkValue, setSearchSkValue] = useState();
    const [decouncedSearchSkValue, setDecouncedSearchSkValue] = useState();
    const [selectedPresetId, setSelectedPresetId] = useState(null);

    const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(parentAutoRefreshEnabled)

    const [selectedDate, setSelectedDate] = useState();

    useEffect(() => {
        setAutoRefreshEnabled(parentAutoRefreshEnabled)

    }, [parentAutoRefreshEnabled])

    useEffect(() => {
        if (subscriptionHccId) {
            setSelectedSearchOption({ value: 'hccId', label: 'hccId' })
            setSearchPkValue(subscriptionHccId)
            setDecouncedSearchPkValue(subscriptionHccId)
        }

    }, [subscriptionHccId])

    const { ref, inView } = useInView();

    const activePreset = filterPresets.find((p) => p.id === selectedPresetId)

    const queryKey = ['benefit-plan-table', selectedDate, selectedSearchOption?.value, decouncedSearchPkValue, decouncedSearchSkValue];

    const {
        data,
        refetch,
        fetchNextPage,
        fetchPreviousPage,
        isLoading, //first time only
        isFetching,
        hasNextPage,
        isFetched,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isError,
        status,
        error
    } = useInfiniteQuery({
        retry: false,
        refetchInterval: autoRefreshEnabled ? 1000 * 3 : false,
        refetchOnWindowFocus: true,
        queryKey,
        queryFn: ({ pageParam }) => {
            let params = { lastEvaluatedKey: pageParam };

            if (['typeKeyClientIdIndex', 'isFromIndex'].includes(selectedSearchOption?.value)) {
                params.indexName = selectedSearchOption?.value
            }

            if (decouncedSearchPkValue && selectedSearchOption?.value) {
                // params[selectedSearchOption?.value] = decouncedSearchValue;

                if (decouncedSearchSkValue) {
                    params.skKey = selectedSearchOption?.additional?.skKey
                    params.skValue = decouncedSearchSkValue
                }

                return fetchBenefitPlanTableItemsByPk({
                    ...params,
                    pkKey: selectedSearchOption?.additional?.pkKey,
                    pkValue: decouncedSearchPkValue
                })


            }



            return fetchBenefitPlanTableScan(params)
        },
        select: (data) => {
            return {
                pages: data.pages.map((page) => page.Items?.map(item => {
                    if (item.requestBody) {
                        try {
                            item.requestBodyParsed = JSON.parse(item.requestBody)
                        } catch (err) {
                            console.log(err)
                        }
                    }

                    if (item.startedAt && item.completedAt) {
                        const date1 = new Date(item.startedAt);
                        const date2 = new Date(item.completedAt);
                        item.duration = getTimeDiff({ date1, date2 })
                    }
                    return item
                })).flatMap((x) => x),
                pageParams: data.pageParams,
            };
            // return { ...data.hits, timestamp: Date.now() }
        },
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.LastEvaluatedKey ? encodeURIComponent(JSON.stringify(lastPage.LastEvaluatedKey)) : null
        },
        staleTime: 0,//,Infinity,
        enabled: !activePreset?.pkPlaceholder || !!decouncedSearchPkValue,
    })

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchPkValue(event.target.value?.trim());
        }, 500),
        []
    );

    const debouncedSkChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchSkValue(event.target.value?.trim());
        }, 500),
        []
    );

    const applyPreset = (preset) => {
        const option = byValue(preset.optionValue)
        setSelectedPresetId(preset.id)
        setSelectedSearchOption(option)
        const pk = preset.pk ?? ''
        const sk = preset.sk ?? ''
        setSearchPkValue(pk)
        setDecouncedSearchPkValue(pk)
        setSearchSkValue(sk)
        setDecouncedSearchSkValue(sk)
    }

    const clearFilters = () => {
        setSelectedPresetId(null)
        setSelectedSearchOption(null)
        setSearchPkValue('');
        setDecouncedSearchPkValue('');
        setSearchSkValue('');
        setDecouncedSearchSkValue('')
    }


    const items = data?.pages.flat();

    let columns = activePreset?.columns || defaultColumns;

    if (showMoreColumns) {
        columns = [
            ...columns,
            'isFrom',
            'typeKey',
            'networkAffiliation',
            'networkConfigs',
            'clientCoverage',
            'planStartDate',
            'planEndDate',
            'planTerminationDate'
        ].filter((col, i, arr) => arr.indexOf(col) === i);
    }

    const today = new Date();
    // today.setDate(today.getDate() + 1);

    return (
        <div className="container">
            {!subscriptionHccId &&
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                        <p className="title">Benefit Plans Table</p>
                        <p className="sub-title">Dynamo BenefitPlans. Click a filter to load OE configs, networks, or plan affiliation.</p>


                    </div>
                    <div>
                        <ToggleSwitch
                            label={`Auto Refresh`}
                            onToggle={(val) => setAutoRefreshEnabled(val)}
                            initialState={autoRefreshEnabled}
                        />
                    </div>

                </div>

            }


            {!subscriptionHccId && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem', }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap', }}>

                    <Select
                        name={'searchOptions'}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        options={searchOptions}
                        value={selectedSearchOption}
                        onChange={(option) => {
                            setSelectedPresetId(null);
                            setSelectedSearchOption(option);
                            if (option.value === 'isFromIndex') {
                                setSearchPkValue('HRP');
                                setDecouncedSearchPkValue('HRP')
                            } else if (option.value === 'typeKeyClientIdIndex') {
                                setSearchPkValue('oe_config');
                                setDecouncedSearchPkValue('oe_config');
                                setSearchSkValue('');
                                setDecouncedSearchSkValue('');
                            } else {
                                setSearchPkValue('');
                                setDecouncedSearchPkValue('');
                                setSearchSkValue('');
                                setDecouncedSearchSkValue('')
                            }
                        }}
                    />


                    {selectedSearchOption?.value && <> <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder={activePreset?.pkPlaceholder || selectedSearchOption?.additional?.pkKey}
                        autoComplete="off"
                        onChange={(e) => {

                            setSearchPkValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchPkValue}
                    />
                        <input
                            style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                            type="text"
                            id={"sk"}
                            placeholder={activePreset?.skPlaceholder || selectedSearchOption?.additional?.skKey}
                            autoComplete="off"
                            onChange={(e) => {

                                setSearchSkValue(e.target.value);
                                debouncedSkChangeHandler(e);
                            }}
                            value={searchSkValue}
                        />
                    </>
                    }

                    <ul className="badges" style={{ padding: 0, margin: 0, flex: '1 1 100%' }}>
                        {filterPresets.map((preset) => (
                            <li
                                key={preset.id}
                                className={`badge ${selectedPresetId === preset.id ? 'active' : ''}`}
                                onClick={() => applyPreset(preset)}
                                title={preset.hint}
                            >
                                {preset.label}
                            </li>
                        ))}
                    </ul>
                    {activePreset?.hint && (
                        <p className="sub-title" style={{ margin: 0, flex: '1 1 100%' }}>{activePreset.hint}</p>
                    )}

                </div>
            </div>}


            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem", }}>
                {subscriptionHccId ? <p></p> : <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={clearFilters}
                >
                    Clear filters
                </p>}

                <p style={{ cursor: "pointer", color: "var(--green)" }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                    {showMoreColumns ? "Less Columns" : "More Columns"}
                </p>
            </div>


            {activePreset?.pkPlaceholder && !decouncedSearchPkValue ? (
                <p className="sub-title">{activePreset.hint}</p>
            ) : status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={"benefitplans-table"}
                        columns={columns}
                        items={items}
                        activeDraftId={null}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        // onDetailHandler={onDetailHandler}
                        ref={ref}
                    />
                </>
            )}


        </div>
    )
}

export default BenefitPlansTable